import React, { useEffect, useRef, useState } from "react";
import { storyblokEditable, SbBlokData } from "@storyblok/react";
import { useNavigate } from "react-router-dom";
import { ImageModel } from "../Models/ImageModel";
import SearchUtils from "../../Search/SearchUtils";
import SearchItemUtils from "../../Search/SearchItem/SearchItemUtils";
import { SearchContentModel } from "../../Search/SearchContent/SearchContentModel";

import "./NavSearch.css";
import Utils from "../Utils/Utils";

export interface NavSearchModel extends SbBlokData {
  image: ImageModel;
}

interface NavSearchProps {
  blok: NavSearchModel;
  onClick?: () => void;
}

const NavSearch: React.FC<NavSearchProps> = ({ blok, onClick }) => {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [data, setData] = useState<SearchContentModel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [iconClicked, setIconClicked] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const url = new URL(
    `search?query=${searchTerm}&page=1`,
    window.location.origin
  ).href;

  const handleClickOutside = (event: MouseEvent) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target as Node)
    ) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setIconClicked(false);
    }
    if (
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target as Node)
    ) {
      setIconClicked(false);
    }
  };

  const handleClick = (e: React.MouseEvent, item: SearchContentModel) => {
    e.preventDefault();
    if (item.full_slug) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      navigate(`/${item.full_slug}`);
      setIconClicked(false);
      if (onClick) {
        onClick();
      }
    }
  };
  const handleIconClick = () => {
    setIconClicked(!iconClicked);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 1);
  };

  const handleViewAllClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchTerm("");
    setDebouncedSearchTerm("");
    navigate(`/search?query=${searchTerm}&page=1`);
    if (onClick) {
      onClick();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      SearchUtils.getCachedOrStoryblokSearch(debouncedSearchTerm, 3, 1)
        .then((response: any) => {
          setTotalItems(response?.data?.total || response?.headers?.total);
          setData(response?.data?.story || response?.data?.stories);
        })
        .catch((error) => {
          if (error.status === 404) {
            navigate("/404");
          } else if (error.status === 500) {
            navigate("/500");
          }
        });
    } else {
      setTotalItems(0);
    }
  }, [debouncedSearchTerm, navigate]);

  return (
    <div
      className={`nav-search-container  ${iconClicked ? "show" : ""}`}
      {...storyblokEditable(blok)}
    >
      <img
        className="nav-search-icon"
        src={Utils.getCacheUrl(blok.image.filename)}
        alt=""
        onClick={handleIconClick}
        onError={(e) => Utils.handleImageError(e, blok.image.filename)}
      />
      <input
        ref={searchInputRef}
        type="text"
        className="input-field"
        value={searchTerm}
        onChange={handleInputChange}
      ></input>
      {debouncedSearchTerm && (
        <div className="nav-search-dropdown-container">
          <div ref={searchRef}>
            <div className="nav-search-header">
              <div className="search-header-left">
                SEARCH: “{searchTerm?.toUpperCase()}”
              </div>
              <div className="search-header-right">{totalItems} RESULTS</div>
            </div>
            {data &&
              data.map((item) => (
                <div
                  onClick={(e) => handleClick(e, item)}
                  className="nav-search-item"
                  key={`nav-search-item${item.full_slug}`}
                >
                  <div className="item-image">
                    {item.content.image?.filename && (
                      <img
                        src={Utils.getCacheUrl(item.content.image?.filename)}
                        alt=""
                        onError={(e) =>
                          Utils.handleImageError(
                            e,
                            item.content.image?.filename
                          )
                        }
                      />
                    )}
                  </div>
                  <div className="item-content">
                    <div className="item-title">
                      <div>{item.content.shortTitle}</div>

                      {SearchItemUtils.getButtonLabel(item) && (
                        <div className="item-action">
                          <span>{SearchItemUtils.getButtonLabel(item)}</span>
                        </div>
                      )}
                    </div>
                    <div className="item-subtitle">
                      {item.content.standfirst}
                    </div>
                  </div>
                </div>
              ))}
            {searchTerm && (
              <div className="nav-search-footer">
                <a href={url} onClick={handleViewAllClick}>
                  VIEW ALL RESULTS
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavSearch;
